import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { 
  Trophy, Play, ChevronRight, ChevronLeft, Share2, Copy, CheckCircle,
  ExternalLink, Twitter, Star, MapPin, Mail, Users, ArrowRight, Ruler,
  GraduationCap, Zap
} from 'lucide-react';
import { API } from '../lib/constants';

const DemoHybrid = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [offers, setOffers] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [stats, setStats] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/profile/dcook1`);
        setProfile(res.data);
        setHighlights(res.data.highlights || []);
        const uid = res.data.profile?.user_id;
        if (uid) {
          const [oR, sR] = await Promise.all([
            axios.get(`${API}/api/dashboard/${uid}/offers`).catch(() => ({ data: { offers: [] } })),
            axios.get(`${API}/api/dashboard/${uid}/comprehensive-stats`).catch(() => ({ data: { stats: [] } })),
          ]);
          setOffers(oR.data.offers || []);
          setStats(sR.data.stats || []);
        }
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  const pages = ['hero', 'recruiting', 'film', 'about'];
  const totalPages = pages.length;
  const next = () => setCurrentPage(p => Math.min(p + 1, totalPages - 1));
  const prev = () => setCurrentPage(p => Math.max(p - 1, 0));

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? next() : prev(); }
    setTouchStart(null);
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#16A34A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const p = profile.profile;
  const m = profile.measurables || {};

  const pageLabels = ['Profile', 'Recruiting', 'Film', 'About'];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-hidden select-none">
      {/* Demo Banner */}
      <div className="bg-[#0F5132] text-white text-center py-2 px-4 text-xs font-bold z-50 relative">
        ⚡ DEMO: Hybrid Layout (Swipeable Bento) — <button onClick={() => navigate('/')} className="underline">Back to Home</button>
        {' '}| <button onClick={() => navigate('/demo/card')} className="underline">Card</button>
        {' '}| <button onClick={() => navigate('/demo/bento')} className="underline">Bento</button>
      </div>

      {/* Main Container */}
      <div className="h-[calc(100vh-32px)] relative" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        
        {/* Page Indicator - top bar */}
        <div className="absolute top-0 left-0 right-0 z-40 flex">
          {pageLabels.map((label, i) => (
            <button key={i} onClick={() => setCurrentPage(i)}
              className={`flex-1 py-3 text-center text-xs font-bold transition-all border-b-2 ${
                i === currentPage 
                  ? 'text-[#16A34A] border-[#16A34A]' 
                  : 'text-white/20 border-transparent hover:text-white/40'
              }`}>
              {label}
            </button>
          ))}
        </div>

        {/* Navigation Arrows */}
        {currentPage > 0 && (
          <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 z-40 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-all">
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {currentPage < totalPages - 1 && (
          <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 z-40 w-10 h-10 bg-[#16A34A]/20 backdrop-blur-sm rounded-full flex items-center justify-center text-[#16A34A] hover:bg-[#16A34A]/30 transition-all">
            <ChevronRight className="h-5 w-5" />
          </button>
        )}

        {/* Swipeable Pages */}
        <div className="h-full flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentPage * 100}%)` }}>
          
          {/* ═══════════════════════════════════ */}
          {/* PAGE 1: HERO BENTO                 */}
          {/* ═══════════════════════════════════ */}
          <div className="w-full h-full flex-shrink-0 overflow-y-auto pt-12 pb-6 px-3 lg:px-8">
            <div className="max-w-5xl mx-auto grid grid-cols-4 lg:grid-cols-12 gap-2.5 lg:gap-3 auto-rows-auto">
              
              {/* Photo tile */}
              <div className="col-span-4 lg:col-span-5 lg:row-span-3 rounded-3xl overflow-hidden relative bg-white/5 aspect-[3/4] lg:aspect-auto">
                {p.profile_picture_url ? (
                  <img src={p.profile_picture_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#16A34A] to-green-600 flex items-center justify-center">
                    <span className="text-6xl font-black text-black">{p.name?.charAt(0)}</span>
                  </div>
                )}
                {/* Name overlay on mobile */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-5 lg:hidden">
                  <h1 className="text-4xl font-black leading-[0.9] tracking-tight">{p.name}</h1>
                  <p className="text-white/50 text-sm mt-1">{p.school}</p>
                </div>
              </div>

              {/* Name tile - desktop only */}
              <div className="hidden lg:flex col-span-7 bg-[#1a1a1a] rounded-3xl p-8 flex-col justify-center">
                <div className="flex gap-2 mb-3">
                  {p.positions?.map((pos, i) => (
                    <span key={i} className="bg-[#0F5132] text-white text-sm font-black px-4 py-1.5 rounded-full">{pos}</span>
                  ))}
                  {p.jersey_number && (
                    <span className="bg-white/10 text-white/50 text-sm font-bold px-4 py-1.5 rounded-full">#{p.jersey_number}</span>
                  )}
                </div>
                <h1 className="text-6xl xl:text-7xl font-black leading-[0.85] tracking-tight mb-2">{p.name}</h1>
                <p className="text-white/40 text-lg">{p.school}</p>
                <div className="flex items-center gap-3 text-white/30 text-sm mt-1">
                  <span>Class of {p.class_year}</span>
                  {p.city && <><span>•</span><MapPin className="h-3.5 w-3.5" /><span>{p.city}, {p.state}</span></>}
                </div>
              </div>

              {/* Position badges - mobile */}
              <div className="col-span-4 flex gap-2 lg:hidden">
                {p.positions?.map((pos, i) => (
                  <span key={i} className="bg-[#0F5132] text-white text-sm font-black px-4 py-2 rounded-full">{pos}</span>
                ))}
              </div>

              {/* Stat tiles */}
              {[
                { value: p.height, label: 'HEIGHT' },
                { value: p.weight ? `${p.weight}` : null, label: 'WEIGHT', unit: 'lbs' },
                { value: p.gpa, label: 'GPA' },
                { value: m.forty_yard_dash ? `${m.forty_yard_dash}s` : null, label: '40-YARD' },
              ].filter(s => s.value).map((stat, i) => (
                <div key={i} className="col-span-2 lg:col-span-2 bg-white/[0.04] border border-white/10 rounded-2xl p-4 lg:p-5 flex flex-col items-center justify-center">
                  <p className="text-2xl lg:text-3xl font-black">{stat.value} {stat.unit && <span className="text-base text-white/30">{stat.unit}</span>}</p>
                  <p className="text-white/30 text-[10px] lg:text-xs uppercase tracking-[0.15em]">{stat.label}</p>
                </div>
              ))}

              {/* Social / action tiles */}
              <div className="col-span-4 lg:col-span-5 flex gap-2 flex-wrap">
                <button className="bg-[#0F5132] text-white px-5 py-2.5 rounded-full text-sm font-bold flex items-center gap-1.5">
                  <Users className="h-4 w-4" /> Follow
                </button>
                <button className="bg-white/10 text-white px-5 py-2.5 rounded-full text-sm font-semibold flex items-center gap-1.5 hover:bg-white/20">
                  <Mail className="h-4 w-4" /> Message
                </button>
                <button onClick={() => { navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  className="bg-white/10 text-white px-5 py-2.5 rounded-full text-sm font-semibold flex items-center gap-1.5 hover:bg-white/20">
                  {copied ? <CheckCircle className="h-4 w-4 text-[#16A34A]" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied!' : 'Share'}
                </button>
                {p.hudl_url && (
                  <a href={p.hudl_url} target="_blank" rel="noopener noreferrer"
                    className="bg-white/10 text-white px-5 py-2.5 rounded-full text-sm font-semibold flex items-center gap-1.5 hover:bg-white/20">
                    <ExternalLink className="h-4 w-4" /> HUDL
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════ */}
          {/* PAGE 2: RECRUITING BENTO            */}
          {/* ═══════════════════════════════════ */}
          <div className="w-full h-full flex-shrink-0 overflow-y-auto pt-12 pb-6 px-3 lg:px-8">
            <div className="max-w-5xl mx-auto grid grid-cols-4 lg:grid-cols-12 gap-2.5 lg:gap-3">
              
              {/* Offers tile */}
              <div className="col-span-4 lg:col-span-7 bg-white/[0.03] border border-white/10 rounded-3xl p-5 lg:p-8">
                <div className="flex items-center gap-2 mb-5">
                  <Trophy className="h-6 w-6 text-[#16A34A]" />
                  <h2 className="text-2xl lg:text-4xl font-black">Offers</h2>
                  {offers.length > 0 && <Badge className="bg-[#16A34A]/20 text-[#16A34A] border-0 text-sm font-bold ml-2">{offers.length}</Badge>}
                </div>
                {offers.length === 0 ? (
                  <p className="text-white/20 text-sm py-8 text-center">No offers yet</p>
                ) : (
                  <div className="space-y-2">
                    {offers.map((offer, i) => (
                      <div key={i} className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-4 flex items-center gap-4 hover:border-white/10 transition-all">
                        {offer.college?.logoUrl ? (
                          <img src={offer.college.logoUrl} alt="" className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl object-contain bg-white p-1.5" />
                        ) : (
                          <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl bg-white/10 flex items-center justify-center text-white/20 font-bold">?</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-base lg:text-lg">{offer.college?.name || 'University'}</p>
                          <p className="text-white/30 text-xs lg:text-sm">{offer.college?.division}</p>
                        </div>
                        <Badge className={`text-xs lg:text-sm font-bold border-0 px-3 py-1 ${
                          offer.status === 'committed' ? 'bg-green-500 text-white' :
                          offer.status === 'offer' ? 'bg-[#0F5132] text-white' : 'bg-white/10 text-white/40'
                        }`}>{offer.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick stats recap */}
              <div className="col-span-4 lg:col-span-5 space-y-2.5 lg:space-y-3">
                {/* Season stats preview */}
                {stats.length > 0 && (
                  <div className="bg-[#16A34A]/10 border border-[#16A34A]/20 rounded-3xl p-5 lg:p-6">
                    <h3 className="text-[#16A34A] text-xs font-bold uppercase tracking-wider mb-3">{stats[0].season} • {stats[0].games_played} Games</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {stats[0].offensive_stats && Object.entries(stats[0].offensive_stats).filter(([_,v]) => v && v !== 0).slice(0, 4).map(([key, val]) => (
                        <div key={key}>
                          <p className="text-2xl lg:text-3xl font-black text-white">{val}</p>
                          <p className="text-white/30 text-[10px] uppercase tracking-wider">{key.replace(/_/g, ' ')}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tagline */}
                {p.tagline && (
                  <div className="bg-[#16A34A] rounded-2xl p-5 text-center">
                    <p className="text-black font-black text-base lg:text-lg italic">"{p.tagline}"</p>
                  </div>
                )}

                {/* GPA + Academics */}
                {p.gpa && (
                  <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 flex items-center gap-4">
                    <GraduationCap className="h-6 w-6 text-[#16A34A]" />
                    <div>
                      <p className="text-3xl font-black">{p.gpa}</p>
                      <p className="text-white/30 text-xs">GPA</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════ */}
          {/* PAGE 3: FILM BENTO                  */}
          {/* ═══════════════════════════════════ */}
          <div className="w-full h-full flex-shrink-0 overflow-y-auto pt-12 pb-6 px-3 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-2 mb-5">
                <Play className="h-6 w-6 text-[#16A34A]" />
                <h2 className="text-2xl lg:text-4xl font-black">Highlights</h2>
                <Badge className="bg-white/10 text-white/50 border-0 text-sm ml-2">{highlights.length}</Badge>
              </div>

              {highlights.length === 0 ? (
                <div className="text-center py-20">
                  <Play className="h-16 w-16 text-white/5 mx-auto mb-4" />
                  <p className="text-white/20">No highlights yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {highlights.map((vid, i) => (
                    <a key={i} href={vid.hudl_video_url} target="_blank" rel="noopener noreferrer"
                      className={`block bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden hover:border-[#16A34A]/30 transition-all group ${
                        i === 0 ? 'lg:col-span-2' : ''
                      }`}>
                      <div className={`${i === 0 ? 'aspect-video' : 'aspect-video'} relative bg-black`}>
                        {vid.og_image ? (
                          <img src={vid.og_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-white/5">
                            <Play className="h-10 w-10 text-white/10" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-5">
                          <p className="text-white font-bold text-base lg:text-lg">{vid.video_title || `Highlight ${i + 1}`}</p>
                          {vid.og_description && <p className="text-white/40 text-xs mt-0.5 line-clamp-1">{vid.og_description}</p>}
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-16 h-16 bg-[#16A34A] rounded-full flex items-center justify-center shadow-lg">
                            <Play className="h-7 w-7 text-black ml-1" />
                          </div>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}

              {/* Full season stats below */}
              {stats.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="h-5 w-5 text-[#16A34A]" />
                    <h3 className="text-xl font-black">{stats[0].season} Stats</h3>
                    <Badge className="bg-white/10 text-white/40 border-0 text-xs">{stats[0].games_played} GP</Badge>
                  </div>
                  <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
                    {stats[0].offensive_stats && Object.entries(stats[0].offensive_stats).filter(([_,v]) => v && v !== 0).map(([key, val]) => (
                      <div key={key} className="bg-white/[0.03] border border-white/10 rounded-xl p-3 text-center">
                        <p className="text-xl lg:text-2xl font-black">{val}</p>
                        <p className="text-white/30 text-[9px] uppercase tracking-wider">{key.replace(/_/g, ' ')}</p>
                      </div>
                    ))}
                    {stats[0].defensive_stats && Object.entries(stats[0].defensive_stats).filter(([_,v]) => v && v !== 0).map(([key, val]) => (
                      <div key={key} className="bg-white/[0.03] border border-white/10 rounded-xl p-3 text-center">
                        <p className="text-xl lg:text-2xl font-black">{val}</p>
                        <p className="text-white/30 text-[9px] uppercase tracking-wider">{key.replace(/_/g, ' ')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ═══════════════════════════════════ */}
          {/* PAGE 4: ABOUT / CTA BENTO           */}
          {/* ═══════════════════════════════════ */}
          <div className="w-full h-full flex-shrink-0 overflow-y-auto pt-12 pb-6 px-3 lg:px-8">
            <div className="max-w-5xl mx-auto grid grid-cols-4 lg:grid-cols-12 gap-2.5 lg:gap-3">
              
              {/* About tile */}
              {p.about_me && (
                <div className="col-span-4 lg:col-span-8 bg-white/[0.03] border border-white/10 rounded-3xl p-6 lg:p-8">
                  <h2 className="text-2xl lg:text-3xl font-black mb-4">About</h2>
                  <p className="text-white/60 text-base lg:text-lg leading-relaxed">{p.about_me}</p>
                </div>
              )}

              {/* Measurables */}
              <div className={`${p.about_me ? 'col-span-4 lg:col-span-4' : 'col-span-4 lg:col-span-12'} space-y-2.5`}>
                <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-5 lg:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Ruler className="h-5 w-5 text-[#16A34A]" />
                    <h3 className="text-lg font-black">Measurables</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: m.bench_press ? `${m.bench_press}` : null, l: 'BENCH', u: 'lbs' },
                      { v: m.squat ? `${m.squat}` : null, l: 'SQUAT', u: 'lbs' },
                      { v: m.vertical_jump ? `${m.vertical_jump}"` : null, l: 'VERTICAL' },
                      { v: m.shuttle ? `${m.shuttle}s` : null, l: 'SHUTTLE' },
                      { v: m.broad_jump, l: 'BROAD JUMP' },
                      { v: m.power_clean ? `${m.power_clean}` : null, l: 'P. CLEAN', u: 'lbs' },
                    ].filter(s => s.v).map((stat, i) => (
                      <div key={i} className="bg-white/[0.04] rounded-xl p-3 text-center">
                        <p className="text-xl font-black">{stat.v} {stat.u && <span className="text-xs text-white/30">{stat.u}</span>}</p>
                        <p className="text-white/30 text-[9px] uppercase tracking-wider">{stat.l}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTA tile */}
              <div className="col-span-4 lg:col-span-12 bg-[#16A34A] rounded-3xl p-8 lg:p-10 text-center">
                <div className="max-w-lg mx-auto">
                  <div className="w-12 h-12 bg-[#1a1a1a] rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-[#16A34A] font-black text-xl">O</span>
                  </div>
                  <h3 className="text-[#1a1a1a] font-black text-2xl lg:text-3xl mb-2">Get your own Offer profile</h3>
                  <p className="text-[#1a1a1a]/50 text-sm lg:text-base mb-5">The link in bio built for athletes. Free forever.</p>
                  <button onClick={() => navigate('/create-profile')}
                    className="bg-[#1a1a1a] text-[#16A34A] font-bold px-10 py-4 rounded-full text-base hover:bg-black transition-all">
                    Create Your Profile <ArrowRight className="inline h-5 w-5 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoHybrid;
